import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { registerBetaGate, unregisterBetaGate } from "@/lib/shared/beta-gate";
import { acceptBetaAgreement, useBetaAgreement } from "@/lib/shared/useBetaAgreement";

// Lazy: the modal pulls framer-motion, and this provider sits inside
// ProtectedRoute which App.tsx imports statically — a static import here
// would drag vendor-motion back into the entry chunk that the anonymous
// landing route pays for (landing-page perf).
const BetaAgreementModal = lazy(() =>
  import("./BetaAgreementModal").then((m) => ({ default: m.BetaAgreementModal })),
);

/**
 * Registers a beta-agreement gate that llmProxy checks before every AI call.
 * If the user hasn't accepted yet, shows a modal and blocks the call until
 * they accept. After acceptance, all future calls pass through instantly.
 *
 * Mount this inside ProtectedRoute (after auth is confirmed).
 */
export function BetaAgreementGateProvider({ children }: { children: React.ReactNode }) {
  const { accepted: dbAccepted, loading: betaLoading } = useBetaAgreement();
  const [localAccepted, setLocalAccepted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acceptedRef = useRef(false);
  acceptedRef.current = dbAccepted || localAccepted;

  const pendingRef = useRef<{
    resolve: () => void;
    reject: (err: Error) => void;
  } | null>(null);

  const handleAccept = useCallback(async () => {
    setAccepting(true);
    setError(null);
    try {
      await acceptBetaAgreement();
      setLocalAccepted(true);
      setShowModal(false);
      pendingRef.current?.resolve();
      pendingRef.current = null;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to accept agreement");
    } finally {
      setAccepting(false);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setShowModal(false);
    setError(null);
    pendingRef.current?.reject(new Error("Beta agreement required to use AI features"));
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    if (betaLoading) return;

    registerBetaGate(async () => {
      if (acceptedRef.current) return;

      return new Promise<void>((resolve, reject) => {
        pendingRef.current = { resolve, reject };
        setShowModal(true);
      });
    });

    return () => unregisterBetaGate();
  }, [betaLoading]);

  return (
    <>
      {children}
      <Suspense fallback={null}>
        <BetaAgreementModal
          open={showModal}
          loading={accepting}
          error={error}
          onAccept={handleAccept}
          onDismiss={handleDismiss}
        />
      </Suspense>
    </>
  );
}
